import { isSupportedCountry, parsePhoneNumberFromString } from 'libphonenumber-js';
import { isValidPhoneNumber } from 'libphonenumber-js/max';
import { COUNTRY_CALLING_CODE_MAP, type CountryCode } from '../constants/countries';

export interface PhoneNumberMetadata {
  countryCode: CountryCode;
  callingCode: string | null;
}

/**
 * A helper function that returns the metadata for a phone number based on the provided ISO country code.
 */
export const getPhoneNumberMetadata = (code: CountryCode): PhoneNumberMetadata => ({
  countryCode: code,
  callingCode: getCallingCode(code),
});

/**
 * A helper function that validates a phone number based on the provided ISO country code.
 */
export const validatePhoneNumber = (phoneNumber: string, countryCode?: CountryCode): boolean => {
  if (!phoneNumber) return false;

  // Since Qonto supports more countries than libphonenumber-js, check if the country is supported,
  // If not supported, consider the number valid (skip validation)
  if (countryCode && !isSupportedCountry(countryCode)) {
    return true;
  }

  try {
    return isValidPhoneNumber(phoneNumber, countryCode);
  } catch {
    return false;
  }
};

/**
 * A helper function that takes a phone number and an ISO country code and returns an E.164 format international phone number.
 */
export const parsePhoneNumber = (phoneNumber: string, countryCode: CountryCode): string => {
  if (!isSupportedCountry(countryCode)) {
    return getCallingCode(countryCode) + phoneNumber;
  }

  const parsedPhoneNumber = parsePhoneNumberFromString(phoneNumber, countryCode);
  return parsedPhoneNumber ? parsedPhoneNumber.number : getCallingCode(countryCode) + phoneNumber;
};

/**
 * A helper function that extracts the national number from an E.164 format international phone number.
 */
export const getNationalNumber = (
  phoneNumber: string,
  countryCode: CountryCode
): string | undefined => {
  const code = getCallingCode(countryCode);
  const numberWithoutCode = phoneNumber.replace(code, '');

  if (!isSupportedCountry(countryCode)) {
    return numberWithoutCode;
  }

  const parsed = parsePhoneNumberFromString(phoneNumber, countryCode);
  return parsed ? parsed.nationalNumber : numberWithoutCode;
};

/**
 * A helper function that localizes an E.164 format international phone number.
 */
export const formatNationalNumber = (
  phoneNumber: string,
  countryCode: CountryCode
): string | undefined => {
  const code = getCallingCode(countryCode);
  const numberWithoutCode = phoneNumber.replace(code, '');

  if (!isSupportedCountry(countryCode)) {
    return numberWithoutCode;
  }

  const parsed = parsePhoneNumberFromString(phoneNumber, countryCode);
  return parsed ? parsed.formatNational() : numberWithoutCode;
};

/**
 * A helper function that formats a phone number in international format with spaces.
 * Example: +33176410308 → +33 1 76 41 03 08, +4915123456789 → +49 151 234 56789
 */
export const formatInternationalNumber = (phoneNumber: string): string => {
  const cleanNumber = phoneNumber.replace(/[\s\-()]/g, '');

  try {
    const parsed = parsePhoneNumberFromString(cleanNumber);
    if (parsed) {
      return parsed.formatInternational();
    }
  } catch {
    return phoneNumber;
  }

  return phoneNumber;
};

/**
 * A helper function that returns the calling code for a given ISO country code.
 */
export const getCallingCode = (code: CountryCode): string => {
  const callingCode = COUNTRY_CALLING_CODE_MAP[code];
  return callingCode ? `+${callingCode}` : '';
};

/**
 * A helper function that formats a phone number as `(countryCode) nationalNumber` with
 * locale-aware spacing applied to the national part via libphonenumber-js.
 * Example: countryCode="+33", number="612345678" → "(+33) 6 12 34 56 78"
 * If the number already starts with the country code, the prefix is not duplicated.
 */
export const formatPhoneNumberWithParentheses = (
  countryCode: string | undefined,
  number: string | undefined
): string => {
  if (!countryCode || !number) return number || '';

  const e164 = number.startsWith(countryCode) ? number : `${countryCode}${number}`;

  const parsed = parsePhoneNumberFromString(e164);
  if (parsed) {
    return parsed.formatInternational().replace(countryCode, `(${countryCode})`);
  }

  const national = number.startsWith(countryCode)
    ? number.slice(countryCode.length).trim()
    : number;
  return `(${countryCode}) ${national}`;
};

/**
 * A helper function that guesses the country code from a phone number.
 * If the phone number contains 'X', it will be replaced with '1' to get the country code.
 * Backend should never obfuscate the country code.
 */
export const getCountryCodeGuess = (phoneNumber: string): CountryCode | null => {
  let cleanNumber = phoneNumber.replace(/[\s\-()]/g, '');

  if (phoneNumber.toLowerCase().includes('x')) {
    cleanNumber = cleanNumber.replace(/x/gi, '1');
  }

  const countries = parsePhoneNumberFromString(cleanNumber)?.getPossibleCountries();
  if (countries && countries.length > 0) {
    // oxlint-disable-next-line typescript/consistent-type-assertions
    return countries[0] as CountryCode;
  }
  return null;
};
